import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { axiosPrivate } from 'app/utils';
import { CircularProgress } from '@mui/material';
import { Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface NavigationItem {
  id: string;
  title: string;
  href: string;
}

interface FeaturePickerModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (item: NavigationItem) => void;
}

async function fetchNavigationItems(): Promise<NavigationItem[]> {
  const response = await axiosPrivate.get<{ items: NavigationItem[] }>(
    '/v3/pharmacist/navigation-items'
  );
  return response.data.items;
}

export const FeaturePickerModal = ({
  open,
  onClose,
  onSelect,
}: FeaturePickerModalProps): ReactElement => {
  const { data, isLoading } = useQuery({
    queryKey: ['pharmacist-navigation-items'],
    queryFn: fetchNavigationItems,
    enabled: open,
    staleTime: 5 * 60 * 1000,
    meta: { errorMessage: 'Failed to load navigation items' },
  });

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="vtwo:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Link to Feature</DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="vtwo:flex vtwo:justify-center vtwo:py-8">
            <CircularProgress size={24} />
          </div>
        )}

        {!isLoading && data?.length === 0 && (
          <p className="vtwo:text-sm vtwo:text-gray-500 vtwo:py-4 vtwo:text-center">
            No navigation items available
          </p>
        )}

        {!isLoading && data && data.length > 0 && (
          <ul className="vtwo:divide-y vtwo:divide-gray-100 vtwo:max-h-96 vtwo:overflow-y-auto">
            {data.map((item) => (
              <li
                key={item.id}
                className="vtwo:flex vtwo:items-center vtwo:justify-between vtwo:py-3 vtwo:px-1"
              >
                <div className="vtwo:flex vtwo:flex-col vtwo:min-w-0">
                  <span className="vtwo:text-sm vtwo:font-medium vtwo:text-gray-900 vtwo:truncate">
                    {item.title}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="vtwo:ml-4 vtwo:shrink-0"
                  onClick={() => {
                    onSelect(item);
                    onClose();
                  }}
                >
                  <Plus className="vtwo:h-3 vtwo:w-3" />
                  Add
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};
